/**
 * Fixtures API Module
 *
 * Handles all API requests for fixtures and competitions
 */

/**
 * Build API parameters for fixture request
 *
 * @param {string}  selectedDate        - Selected date in YYYY-MM-DD format
 * @param {string}  selectedCompetition - Selected competition ID
 * @param {boolean} cacheBust           - Whether to add cache-busting parameter (for polling)
 * @return {URLSearchParams} URL parameters
 */
export function buildApiParams(selectedDate, selectedCompetition, cacheBust = false) {
  const params = new URLSearchParams();
  if (selectedDate) {
    params.append('date', selectedDate);
  }
  if (selectedCompetition && selectedCompetition !== 'all') {
    params.append('competition', selectedCompetition);
  }
  // Add cache-busting timestamp for polling requests to bypass WPE object cache
  if (cacheBust) {
    params.append('_', Math.floor(Date.now() / 1000));
  }
  return params;
}

/**
 * Fetch fixtures from API
 *
 * @param {string}  selectedDate        - Selected date in YYYY-MM-DD format
 * @param {string}  selectedCompetition - Selected competition ID
 * @param {boolean} cacheBust           - Whether to add cache-busting parameter (for polling)
 * @return {Promise<Object>} Response object with fixtures data and response headers
 */
export async function fetchFixturesFromApi(selectedDate, selectedCompetition, cacheBust = false) {
  const params = buildApiParams(selectedDate, selectedCompetition, cacheBust);

  const baseEndpoint =
    window.bigBiteStatsPerformEditor?.wp_rest_fixtures || '/big-bite-stats-perform/v1/fixtures';
  const url = `/wp-json${baseEndpoint}${params.toString() ? `?${params.toString()}` : ''}`;

  const nonce = window.wpApiSettings?.nonce;
  if (!nonce) throw new Error('Security token not available');

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'X-WP-Nonce': nonce,
    },
    cache: 'default',
  });

  // Handle 404 as empty fixtures
  if (response.status === 404) {
    return {
      fixtures: { fixtures: [], timestamp: Date.now() / 1000 },
      hasLiveData: false,
    };
  }

  if (!response.ok) {
    throw new Error('Failed to fetch fixtures');
  }

  const fixtures = await response.json();

  return {
    fixtures,
    hasLiveData: response.headers.get('X-Live-Data') === 'true',
  };
}

/**
 * Fetch all available competitions from API
 *
 * @return {Promise<Array>} Array of competition objects
 */
export async function fetchCompetitions() {
  const competitionsEndpoint =
    window.bigBiteStatsPerformEditor?.wp_rest_competitions ||
    '/big-bite-stats-perform/v1/competitions';

  const nonce = window.wpApiSettings?.nonce;
  if (!nonce) throw new Error('Security token not available');

  const url = `/wp-json${competitionsEndpoint}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'X-WP-Nonce': nonce,
    },
    cache: 'default',
  });

  if (!response.ok) {
    throw new Error('Failed to fetch competitions');
  }

  return response.json();
}
